/**
 * PostPreviewUpsell - Completion screen for free-preview activities.
 *
 * Shows personalized upsell with pricing after a free-preview activity
 * is completed. Emits paywall_viewed event for analytics.
 *
 * @see GitHub Issue #1026
 */

export default class PostPreviewUpsell {
    /**
     * Show the post-preview upsell overlay.
     * @param {Object} options
     * @param {string} options.activityTitle - Name of the completed activity
     * @param {number} options.premiumActivityCount - Remaining premium activities
     * @param {Function} [options.onReplay] - Callback for replay action
     * @param {Function} [options.onDismiss] - Callback for dismiss action
     */
    static show ({ activityTitle, premiumActivityCount, onReplay, onDismiss }) {
        // Overlay
        const overlay = document.createElement('div');
        overlay.className = 'activity-completion-overlay';

        // Modal
        const modal = document.createElement('div');
        modal.className = 'post-preview-upsell-modal';

        // Close button
        const closeBtn = document.createElement('button');
        closeBtn.className = 'activity-completion-close';
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', 'Close');
        closeBtn.addEventListener('pointerup', () => {
            PostPreviewUpsell._dismiss(overlay);
            if (onDismiss) {
                onDismiss();
            }
        });
        modal.appendChild(closeBtn);

        // Header
        const header = document.createElement('div');
        header.className = 'activity-completion-header';

        const title = document.createElement('h3');
        title.className = 'activity-completion-title';
        title.textContent = `Great job completing "${activityTitle}"!`;
        header.appendChild(title);
        modal.appendChild(header);

        // Body
        const body = document.createElement('div');
        body.className = 'activity-completion-body';

        const message = document.createElement('p');
        message.className = 'activity-completion-message';
        message.textContent = `${premiumActivityCount} more premium activities await`;
        body.appendChild(message);

        // Pricing section
        const pricing = document.createElement('div');
        pricing.className = 'post-preview-pricing';

        const monthly = document.createElement('div');
        monthly.className = 'post-preview-pricing-option';
        monthly.setAttribute('data-plan', 'monthly');
        monthly.textContent = '$4.99/month';
        pricing.appendChild(monthly);

        const annual = document.createElement('div');
        annual.className = 'post-preview-pricing-option';
        annual.setAttribute('data-plan', 'annual');
        annual.textContent = '$39.99/year';
        pricing.appendChild(annual);

        body.appendChild(pricing);

        // CTA button
        const cta = document.createElement('button');
        cta.className = 'post-preview-upsell-cta';
        cta.textContent = 'Unlock All Activities';
        cta.addEventListener('pointerup', () => {
            PostPreviewUpsell._dismiss(overlay);
            if (typeof window.SubscriptionManager !== 'undefined' &&
                typeof window.SubscriptionManager.showUpsellModal === 'function') {
                window.SubscriptionManager.showUpsellModal(document.body, {
                    onSubscribe: (plan) => {
                        if (typeof window.SubscriptionManager.startCheckout === 'function') {
                            return window.SubscriptionManager.startCheckout(plan);
                        }
                        return Promise.resolve();
                    },
                    onDismiss: () => {}
                }, { paywallType: 'post_preview_completion' });
            }
        });
        body.appendChild(cta);

        // Replay button
        if (onReplay) {
            const replayBtn = document.createElement('button');
            replayBtn.className = 'activity-completion-btn-replay';
            replayBtn.innerHTML = '&#8634; Replay';
            replayBtn.setAttribute('aria-label', 'Replay activity');
            replayBtn.addEventListener('pointerup', () => {
                PostPreviewUpsell._dismiss(overlay);
                onReplay();
            });
            body.appendChild(replayBtn);
        }

        modal.appendChild(body);
        overlay.appendChild(modal);
        document.body.appendChild(overlay);

        // Emit paywall_viewed event
        if (window.ScratchJrEventTracker && window.ScratchJrEventTracker.track) {
            window.ScratchJrEventTracker.track('paywall_viewed', {
                paywallType: 'post_preview_completion'
            });
        }
    }

    /**
     * Dismiss the overlay with fade-out.
     * @private
     */
    static _dismiss (overlay) {
        if (overlay) {
            overlay.style.opacity = '0';
            setTimeout(() => {
                if (overlay && overlay.parentNode) {
                    overlay.parentNode.removeChild(overlay);
                }
            }, 300);
        }
    }
}
